export type OpKind = "obj" | "emitter" | "conn" | "settings";
export type OpAction = "upsert" | "remove";

export const SETTINGS_ID = "settings";

/** Default / clamp range for a room's player capacity, shared by the create UI, the worker, and the directory. */
export const DEFAULT_ROOM_CAPACITY = 24;
export const MIN_ROOM_CAPACITY = 2;
export const MAX_ROOM_CAPACITY = 24;
export const PUBLIC_ROOM_CAP = 3;

/**
 * Kick-vote weighting + healing, shared by the worker tally and the client's gate.
 *
 * A kicker's attack power ramps with room stay AND scales with room population N
 * (present participants excluding the target): nothing for the first KICK_MIN_STAY_MS
 * (10 min), reaching a base vote of 1 at KICK_BASE_STAY_MS (20 min), then climbing to
 * max(1, N / KICK_POP_DIVISOR) by KICK_MAX_STAY_MS (70 min). The kick threshold is a
 * majority of the room: kickThreshold(N) = N / 2. Because KICK_POP_DIVISOR is 6,
 * 3 full-stay voters always reach 3·(N/6) = N/2 — so "3 long-stay regulars" evicts in
 * any room size, while 20-min voters need a majority (N/2 of them). A single fresh
 * joiner (power 0 below 10 min) can never evict anyone.
 *
 * Each vote is a hit that heals: its contribution decays linearly to 0 over
 * KICK_HEAL_MS (1 hour) from when it landed, so a target fully recovers an hour after
 * the last vote. The DO sums the decayed hits per target and severs once the sum
 * reaches kickThreshold(N). Tunable from here; the rationale is why these aren't inline.
 */
export const KICK_MIN_STAY_MS = 600_000;
export const KICK_BASE_STAY_MS = 1_200_000;
export const KICK_MAX_STAY_MS = 4_200_000;
export const KICK_POP_DIVISOR = 6;
export const KICK_HEAL_MS = 3_600_000;

/**
 * Re-join cooldown imposed on a kicked uid, server-authoritative and global: a uid
 * evicted by an owner or a passing kick vote must wait before its next `hello` is
 * admitted, into any room (the ban keys on the stable uid, not the room). The wait
 * doubles per cumulative kick — COOLDOWN_BASE_MS (1 min) for the first, then ×2 each
 * time, capped at COOLDOWN_CAP_MS (60 min): 1, 2, 4, 8, 16, 32, 60, 60… min. The
 * kick count resets to 0 once COOLDOWN_DECAY_MS (24 h) passes with no further kick,
 * so an occasional offender is not punished forever; the decay is applied when the
 * next kick is recorded (the only place the count is consumed). Tunable from here;
 * the rationale is why these aren't inline. Lives in the shared protocol module so
 * the worker's CooldownRegistry computes the wait from the same source the client
 * formats its remaining-time modal against.
 */
export const COOLDOWN_BASE_MS = 60_000;
export const COOLDOWN_CAP_MS = 3_600_000;
export const COOLDOWN_DECAY_MS = 86_400_000;

/** The re-join wait after a uid's `count`-th cumulative kick: 0 for a never-kicked uid, else COOLDOWN_BASE_MS×2^(count-1) capped at COOLDOWN_CAP_MS. */
export function cooldownMs(count: number): number {
  if (count <= 0) {
    return 0;
  }
  return Math.min(COOLDOWN_CAP_MS, COOLDOWN_BASE_MS * 2 ** (count - 1));
}

/**
 * How long the room DO holds a kicked connection open after sending its `kicked`
 * reject instead of closing immediately. During this window the target self-undoes
 * its session edits and the resulting revert ops apply + broadcast to the room, so
 * the griefer's leftovers vanish for everyone before the socket drops. The DO closes
 * early once the target sends {@link KickDoneMsg}; this timeout only backstops a
 * client that never reports done (stale / hostile / broken), guaranteeing eviction.
 */
export const KICK_GRACE_MS = 3_000;

/**
 * A kicker's vote weight from their room stay and the room population: 0 below
 * KICK_MIN_STAY_MS, 0→1 to KICK_BASE_STAY_MS, then 1→max(1, population / KICK_POP_DIVISOR)
 * to KICK_MAX_STAY_MS (flat past it).
 */
export function attackPower(stayMs: number, population: number): number {
  if (stayMs <= KICK_MIN_STAY_MS) {
    return 0;
  }
  const maxWeight = Math.max(1, population / KICK_POP_DIVISOR);
  if (stayMs >= KICK_MAX_STAY_MS) {
    return maxWeight;
  }
  if (stayMs <= KICK_BASE_STAY_MS) {
    return (stayMs - KICK_MIN_STAY_MS) / (KICK_BASE_STAY_MS - KICK_MIN_STAY_MS);
  }
  const t = (stayMs - KICK_BASE_STAY_MS) / (KICK_MAX_STAY_MS - KICK_BASE_STAY_MS);
  return 1 + t * (maxWeight - 1);
}

/** Kick threshold = a majority of the room: half the eligible population (present excluding the target). */
export function kickThreshold(population: number): number {
  return population / 2;
}

/** Remaining un-healed fraction of a vote's damage `ageMs` after it landed: 1 at impact, linearly to 0 over KICK_HEAL_MS. */
export function healFactor(ageMs: number): number {
  return Math.max(0, 1 - ageMs / KICK_HEAL_MS);
}

/**
 * The reward-ad proof a joiner presents in its hello. A stub fixed value: the
 * client sets it after the user clears the ad-gate modal and the room DO admits
 * only a hello carrying exactly this string. Swapping in a real ad SDK means the
 * client mints this from a completed-view receipt and the DO verifies that receipt
 * instead of the literal — at which point a blocked/dismissed ad yields no token
 * and the DO refuses the join, so the gate cannot be bypassed from devtools.
 */
export const AD_TOKEN_STUB = "ad-stub-v1";

/**
 * The minimal Community-scene provenance a room carries so every participant —
 * including a late joiner who never opened the source card — can show the same
 * scene-info chip (title / author). Set write-once by the room creator and rebroadcast
 * on the join snapshot; null when the room was not opened from a Community scene.
 */
export interface RoomSceneMeta {
  id: string;
  title: string;
  ownerName: string | null;
  owner: string;
}

/**
 * One per-key delta on the wire: an upsert or remove of a single object,
 * emitter, connection, or the room settings. `value` is the entity's serialized
 * record (the same shape the scene serializer emits) on upsert, absent on
 * remove. `version` rides on every op so the room DO can do last-write-wins.
 * `owner` is the sender's session slot, never sent by the client — the DO stamps
 * it from the connection's authoritative identity before relaying (spoof-proof,
 * and absent on the client→DO send to save the bytes).
 */
export interface Op {
  t: "op";
  kind: OpKind;
  id: string;
  action: OpAction;
  value?: unknown;
  owner?: number;
  version: number;
  transient?: boolean;
}

/**
 * Client → DO right after connect: announces the sender's stable uid. `public` is
 * set only by the room's creator (the create-room flow); it tells the room DO to
 * mark itself public (write-once) and register with the directory. `name` is the
 * creator-chosen room name, also carried only by the creator and stored write-once.
 * Joiners and surfers omit both so they never flip a room's publicity or rename it.
 * `gateAnon` rides only on the creator's hello (write-once, like `public`/`name`):
 * it marks the room as rejecting anonymous joiners. `token` is the sender's
 * Supabase access token, carried by every client so a gated room's DO can verify
 * the joiner server-side against /auth/v1/user. `capacity` (creator-only,
 * write-once) is the room's max simultaneous players; the DO refuses a join once it
 * is reached. `scene` (creator-only, write-once) carries the source Community
 * scene's provenance so late joiners can show the scene-info chip.
 */
export interface HelloMsg {
  t: "hello";
  uid: string;
  public?: boolean;
  name?: string;
  gateAnon?: boolean;
  token?: string;
  displayName?: string;
  capacity?: number;
  scene?: RoomSceneMeta;
  adToken?: string;
}

/** Client → DO: claim an object for editing (exclusive; held while selected / dragged). */
export interface ClaimMsg {
  t: "claim";
  id: string;
}

/** Client → DO: release a previously claimed object. */
export interface ReleaseMsg {
  t: "release";
  id: string;
}

/**
 * The clock reset, relayed as a one-shot action rather than a state op: the owner
 * presses Reset, the DO echoes it to every connection (the sender included) so all
 * participants reset on the same round-trip instead of the presser resetting early.
 * Owner-only — the DO drops a reset from any non-owner connection. Carries no
 * payload; it triggers each client's local performClockReset.
 */
export interface ResetMsg {
  t: "reset";
}

/**
 * A one-shot emote reaction, relayed like a reset (broadcast-only, never stored):
 * the sender plays it locally on send and the DO echoes it to every other socket
 * so each peer plays it over the sender's avatar. `uid` is filled by the client but
 * the DO overwrites it from the connection's authoritative uid before relaying;
 * `emoji` is dropped unless it is one of the fixed EMOTES set.
 */
export interface EmoteMsg {
  t: "emote";
  uid: string;
  emoji: string;
}

/**
 * Client → DO: a vote to kick another participant. `target` is the victim's uid; the
 * DO trusts the kicker identity from the connection's authoritative uid, never the
 * wire. The tally is server-authoritative — the client only votes, it does not decide
 * the outcome. In an owned (private) room the owner's vote evicts immediately and the
 * owner is never a valid target; otherwise each voter's {@link attackPower} (a hit that
 * heals via {@link healFactor}, deduped to one per kicker) is summed per target and the
 * target is severed once the sum reaches {@link kickThreshold}.
 */
export interface KickMsg {
  t: "kick";
  target: string;
}

/**
 * Client → DO: the kicked target has finished self-undoing and flushing its revert
 * ops, so the DO may close the connection now rather than waiting out
 * {@link KICK_GRACE_MS}. Carries no payload — the DO identifies the sender from the
 * connection's authoritative state and only acts on a connection it is already
 * gracing (one it sent a `kicked` reject to). A non-graced connection's kickDone is
 * a no-op, so it can never sever an un-kicked participant.
 */
export interface KickDoneMsg {
  t: "kickDone";
}

/**
 * A one-shot activity-log line, relayed like an emote (broadcast-only, never stored):
 * the actor performed a shared/privileged room operation and every participant shows a
 * transient "{who} did X" line. The actor fires it locally on send; the DO overwrites
 * `uid` from the connection's authoritative uid before relaying to the other sockets,
 * and drops any `kind` outside the fixed set. `kind` is which operation: a clock reset,
 * a tempo change, or any other shared settings change. `detail` is an optional,
 * language-neutral code (the changed parameter and its value, e.g. the BPM for a
 * tempo change or `gravity|9.8`) that the receiver localizes into the displayed
 * sentence, so a peer in another language still reads it in their own.
 */
export interface ActivityMsg {
  t: "activity";
  uid: string;
  kind: ActivityKind;
  detail?: string;
}

export type ActivityKind = "clockReset" | "tempo" | "settings";

export const ACTIVITY_KINDS: readonly ActivityKind[] = ["clockReset", "tempo", "settings"];

/**
 * A momentary push-button press or release, relayed like an emote (broadcast-only,
 * never stored): the presser drives the button locally on the down/up edge and the
 * DO echoes the gesture to every other socket so each peer runs the same press on
 * its local signal runtime (cap sink + pulse → emitter burst / lamp / sound).
 * `uid` is filled by the client but the DO overwrites it from the connection's
 * authoritative uid before relaying. Toggle buttons never ride this — their latch
 * is synced through the serialized `buttonOn` op, so relaying their press too would
 * double-fire the pulse. `phase` is the press down or up edge.
 */
export interface ButtonMsg {
  t: "button";
  uid: string;
  id: string;
  phase: "press" | "release";
}

/**
 * Owner → DO → other clients: one counter's authoritative value. The owning client
 * (the placer, the only sender) emits `{id, value}` whenever its counter's value
 * changes, throttled to ~1/sec and never on a periodic loop. The DO stamps `owner`
 * from the sender's slot (spoof-proof, like an op; absent on the client→DO send to
 * save the bytes), caches the latest value+owner per id so a late joiner is seeded
 * on the snapshot, and relays to every other socket. Counter values ride this
 * channel rather than the LWW scene op — they change too fast to churn op versions.
 */
export interface CountMsg {
  t: "count";
  id: string;
  value: number;
  owner?: number;
}

/**
 * Client → DO → other clients: reassign one counter's owner without touching its
 * value. Sent the moment a client semi-selects the counter's root cluster in
 * touchplay, so ownership follows whoever is operating the cluster rather than
 * whoever placed it. The DO stamps `owner` from the sender's slot (spoof-proof,
 * absent on the client→DO send to save the bytes), rewrites the counter's cached
 * owner while keeping the cached value so a late joiner is seeded with the current
 * owner, and relays to every other socket. Ownership rides this discrete channel —
 * not the fast value channel — so it stays sticky when the operator moves on.
 */
export interface CountOwnerMsg {
  t: "countOwner";
  id: string;
  owner?: number;
}

/** The fixed emote palette, shared by client radial, send path, and the DO allowlist. */
export const EMOTES = ["👋", "👍", "👏", "🙏", "🎶", "✨", "❤️", "😄", "🤣", "😎", "🥺", "😭"] as const;

/** One participant resolved from a session slot: the stable uid plus display color and name. */
export interface RosterEntry {
  uid: string;
  color: number;
  name: string;
}

/**
 * DO → all clients on any roster change, keyed by session slot (a small per-room
 * integer) rather than the 36-char uid: `colors` maps slot → palette index;
 * `names` maps slot → display name; `claims` maps object id → holder slot. All are
 * ephemeral (rebuilt from live connection state, dropped on disconnect). `owner` is
 * the room creator's slot (`-1` until the creator's hello marks the room) so every
 * client can gate owner-only operations; it rides on presence (not the snapshot) so
 * the owner's entry/exit re-evaluates for everyone. `roster` is a delta: the
 * slot → uid mapping for slots first announced since the last broadcast, so the
 * heavy uid travels once per slot while later presences carry only the cheap slots.
 * `public` flags an ownerless public room (the shared sandbox): the room has no
 * creator-owner, so every participant is unlocked rather than locked as a guest.
 * It rides on presence (not just the snapshot) so a joiner who never set the
 * create-time `public` opt still learns the room type from the server.
 */
export interface PresenceMsg {
  t: "presence";
  colors: Record<number, number>;
  claims: Record<string, number>;
  owner: number;
  names: Record<number, string>;
  roster: Record<number, string>;
  public: boolean;
}

/** Distinct colors assigned round-robin to room members; index wraps past the end. */
export const ROOM_COLORS: number[] = [
  0x4aa3ff, 0xff6b6b, 0x4ade80, 0xfbbf24, 0xc084fc, 0xf472b6, 0x22d3ee, 0xfb923c,
];

/**
 * DO → one client on join: the room's current state assembled from the per-key
 * records, plus each key's version so the joiner seeds its diff baseline and
 * does not echo what it just received back as fresh local edits. `self` is the
 * joiner's own session slot and `roster` the full slot → {uid,color,name} map of
 * everyone present (plus the room owner, even if absent) so the joiner can resolve
 * every slot the following presence/op traffic references — this doubles as the
 * hello-ack. The REST snapshot (directory peek) carries `self: -1` / empty roster.
 * `capacity` and `provenance` ride the snapshot so a late joiner learns the room's
 * player cap and source Community scene without having created the room.
 * `clockSinceResetMs` is the elapsed time (ms) from the room's clock origin (last
 * ClockReset, else room creation) to snapshot build, both read off the one server
 * clock so client↔server skew cancels — the joiner starts its local clock there
 * instead of 0, landing roughly in phase with members already aligned by reset.
 */
export interface SnapshotMsg {
  t: "snapshot";
  objects: unknown[];
  emitters: unknown[];
  connections: unknown[];
  settings: unknown | null;
  versions: Record<string, number>;
  self: number;
  roster: Record<number, RosterEntry>;
  capacity?: number;
  provenance?: RoomSceneMeta | null;
  counts?: Record<string, CountCacheEntry>;
  clockSinceResetMs?: number;
}

/** The DO's cached latest authoritative value + owner slot for one owned counter, seeded onto a late joiner's snapshot. */
export interface CountCacheEntry {
  value: number;
  owner: number;
}

/**
 * DO → one client when the room refuses or severs the connection: `anon-gate` for
 * an anonymous (or token-less) joiner of a gated room, `verify-failed` when
 * /auth/v1/user could not confirm the token (401 / unreachable / Supabase error —
 * fail-closed), `bot-detected` when an in-session connection held one continuous
 * mid-drag stream past the bot threshold, `room-full` when the join would exceed
 * the room's capacity, `ad-required` when a joiner's hello lacks the reward-ad
 * proof (see {@link AD_TOKEN_STUB}), `kicked` when an in-session participant was
 * evicted by an owner or a passing kick vote, `cooldown` when a previously-kicked
 * uid's `hello` arrives before its re-join wait (see {@link cooldownMs}) elapses,
 * `banned` when a uid that was kicked from *this* room tries to rejoin it (a
 * permanent per-room ban, distinct from the global time-based `cooldown` — it has
 * no expiry but only lasts while the room stays alive, since the room DO's ban list
 * is wiped when the room tears down). The DO sends this instead of (or in place of
 * further) traffic and closes the socket; the client surfaces the reason and
 * returns to its local scene. `retryAfterMs` rides only on `cooldown` — the
 * remaining wait in ms, which the client formats into its "rejoin in N min" modal.
 */
export interface RejectMsg {
  t: "rejected";
  reason: "anon-gate" | "verify-failed" | "bot-detected" | "room-full" | "ad-required" | "kicked" | "cooldown" | "banned";
  retryAfterMs?: number;
}

export type ClientMsg = HelloMsg | ClaimMsg | ReleaseMsg | Op | ResetMsg | EmoteMsg | ButtonMsg | CountMsg | CountOwnerMsg | KickMsg | KickDoneMsg | ActivityMsg;
export type ServerMsg = SnapshotMsg | PresenceMsg | RejectMsg | Op | ResetMsg | EmoteMsg | ButtonMsg | CountMsg | CountOwnerMsg | ActivityMsg;

/** The DO storage key for a per-key record. Settings live under one fixed key. */
export function storageKey(kind: OpKind, id: string): string {
  return kind === "settings" ? "room:settings" : `${kind}:${id}`;
}

/** What each storage key holds: the serialized value plus its owner and version. */
export interface StoredRecord {
  value: unknown;
  owner: string;
  version: number;
}

/** One public room in the directory pool: id plus its creator-set name, current occupancy, capacity, creation time (for the busy × recent ordering and the card info), and whether it rejects anonymous joiners (for the gated badge + anon sign-in prompt). */
export interface RoomDescriptor {
  id: string;
  name: string;
  count: number;
  capacity: number;
  createdAt: number;
  gateAnon: boolean;
}

/** Directory DO → client on the one-shot pool GET: the live public rooms with name, occupancy, and creation time. */
export interface DirectoryList {
  rooms: RoomDescriptor[];
}

export type WireMsg = ClientMsg | ServerMsg;

/**
 * Wire type tags: one small integer per message kind on the socket, replacing the
 * string `t`. Ops split into two tags so the `transient` flag never travels — a
 * commit op is `opCommit`, a mid-drag op is `opTransient`.
 */
export const MSG_T = {
  opCommit: 1,
  opTransient: 2,
  hello: 3,
  snapshot: 4,
  presence: 5,
  claim: 6,
  release: 7,
  reset: 8,
  rejected: 9,
  emote: 10,
  button: 11,
  count: 12,
  kick: 13,
  activity: 14,
  kickDone: 15,
  countOwner: 16,
} as const;

const ACTIVITY_ENC: Record<ActivityKind, number> = { clockReset: 0, tempo: 1, settings: 2 };
const ACTIVITY_DEC: ActivityKind[] = ["clockReset", "tempo", "settings"];

const OP_KIND_ENC: Record<OpKind, number> = { obj: 0, emitter: 1, conn: 2, settings: 3 };
const OP_KIND_DEC: OpKind[] = ["obj", "emitter", "conn", "settings"];
const OP_ACTION_ENC: Record<OpAction, number> = { upsert: 0, remove: 1 };
const OP_ACTION_DEC: OpAction[] = ["upsert", "remove"];

/**
 * Serializes an internal message to its compact wire string: integer `t`, integer
 * op kind/action, single-letter op keys (`k`/`a`/`v`/`n`/`i`/`o`), and the
 * transient flag folded into the type tag. The single send-side boundary — every
 * `socket.send` / `connection.send` / `broadcast` goes through here so the rest of
 * the code keeps the readable internal types. Throws on an unknown message so a new
 * union member can never slip onto the wire unencoded.
 */
export function encodeMsg(msg: WireMsg): string {
  switch (msg.t) {
    case "op": {
      const w: Record<string, unknown> = {
        t: msg.transient === true ? MSG_T.opTransient : MSG_T.opCommit,
        k: OP_KIND_ENC[msg.kind],
        i: msg.id,
        a: OP_ACTION_ENC[msg.action],
        n: msg.version,
      };
      if (msg.value !== undefined) {
        w.v = msg.value;
      }
      if (msg.owner !== undefined) {
        w.o = msg.owner;
      }
      return JSON.stringify(w);
    }
    case "hello": {
      const w: Record<string, unknown> = { t: MSG_T.hello, uid: msg.uid };
      if (msg.public !== undefined) {
        w.public = msg.public;
      }
      if (msg.name !== undefined) {
        w.name = msg.name;
      }
      if (msg.gateAnon !== undefined) {
        w.gateAnon = msg.gateAnon;
      }
      if (msg.token !== undefined) {
        w.token = msg.token;
      }
      if (msg.displayName !== undefined) {
        w.displayName = msg.displayName;
      }
      if (msg.capacity !== undefined) {
        w.capacity = msg.capacity;
      }
      if (msg.scene !== undefined) {
        w.scene = msg.scene;
      }
      if (msg.adToken !== undefined) {
        w.adToken = msg.adToken;
      }
      return JSON.stringify(w);
    }
    case "snapshot": {
      const w: Record<string, unknown> = {
        t: MSG_T.snapshot,
        objects: msg.objects,
        emitters: msg.emitters,
        connections: msg.connections,
        settings: msg.settings,
        versions: msg.versions,
        self: msg.self,
        roster: msg.roster,
      };
      if (msg.capacity !== undefined) {
        w.capacity = msg.capacity;
      }
      if (msg.provenance !== undefined) {
        w.provenance = msg.provenance;
      }
      if (msg.counts !== undefined) {
        w.counts = msg.counts;
      }
      if (msg.clockSinceResetMs !== undefined) {
        w.clockSinceResetMs = msg.clockSinceResetMs;
      }
      return JSON.stringify(w);
    }
    case "presence":
      return JSON.stringify({
        t: MSG_T.presence,
        colors: msg.colors,
        claims: msg.claims,
        owner: msg.owner,
        names: msg.names,
        roster: msg.roster,
        public: msg.public,
      });
    case "claim":
      return JSON.stringify({ t: MSG_T.claim, id: msg.id });
    case "release":
      return JSON.stringify({ t: MSG_T.release, id: msg.id });
    case "reset":
      return JSON.stringify({ t: MSG_T.reset });
    case "rejected": {
      const w: Record<string, unknown> = { t: MSG_T.rejected, reason: msg.reason };
      if (msg.retryAfterMs !== undefined) {
        w.r = msg.retryAfterMs;
      }
      return JSON.stringify(w);
    }
    case "emote":
      return JSON.stringify({ t: MSG_T.emote, uid: msg.uid, emoji: msg.emoji });
    case "button":
      return JSON.stringify({ t: MSG_T.button, uid: msg.uid, id: msg.id, phase: msg.phase });
    case "kick":
      return JSON.stringify({ t: MSG_T.kick, target: msg.target });
    case "kickDone":
      return JSON.stringify({ t: MSG_T.kickDone });
    case "activity": {
      const w: Record<string, unknown> = { t: MSG_T.activity, uid: msg.uid, k: ACTIVITY_ENC[msg.kind] };
      if (msg.detail !== undefined) {
        w.d = msg.detail;
      }
      return JSON.stringify(w);
    }
    case "count": {
      const w: Record<string, unknown> = { t: MSG_T.count, i: msg.id, v: msg.value };
      if (msg.owner !== undefined) {
        w.o = msg.owner;
      }
      return JSON.stringify(w);
    }
    case "countOwner": {
      const w: Record<string, unknown> = { t: MSG_T.countOwner, i: msg.id };
      if (msg.owner !== undefined) {
        w.o = msg.owner;
      }
      return JSON.stringify(w);
    }
    default: {
      const exhaustive: never = msg;
      throw new Error(`encodeMsg: unknown message ${JSON.stringify(exhaustive)}`);
    }
  }
}

/**
 * Parses a wire string back to the readable internal message — the single
 * receive-side boundary (every `onmessage` / DO handler entry decodes here).
 * Throws on an unknown wire type so a malformed or future message is surfaced
 * rather than silently dropped.
 */
export function decodeMsg(raw: string): WireMsg {
  const w = JSON.parse(raw) as Record<string, unknown>;
  const t = w.t as number;
  switch (t) {
    case MSG_T.opCommit:
    case MSG_T.opTransient: {
      const op: Op = {
        t: "op",
        kind: OP_KIND_DEC[w.k as number],
        id: w.i as string,
        action: OP_ACTION_DEC[w.a as number],
        version: w.n as number,
      };
      if ("v" in w) {
        op.value = w.v;
      }
      if ("o" in w) {
        op.owner = w.o as number;
      }
      if (t === MSG_T.opTransient) {
        op.transient = true;
      }
      return op;
    }
    case MSG_T.hello: {
      const h: HelloMsg = { t: "hello", uid: w.uid as string };
      if ("public" in w) {
        h.public = w.public as boolean;
      }
      if ("name" in w) {
        h.name = w.name as string;
      }
      if ("gateAnon" in w) {
        h.gateAnon = w.gateAnon as boolean;
      }
      if ("token" in w) {
        h.token = w.token as string;
      }
      if ("displayName" in w) {
        h.displayName = w.displayName as string;
      }
      if ("capacity" in w) {
        h.capacity = w.capacity as number;
      }
      if ("scene" in w) {
        h.scene = w.scene as RoomSceneMeta;
      }
      if ("adToken" in w) {
        h.adToken = w.adToken as string;
      }
      return h;
    }
    case MSG_T.snapshot: {
      const snap: SnapshotMsg = {
        t: "snapshot",
        objects: w.objects as unknown[],
        emitters: w.emitters as unknown[],
        connections: w.connections as unknown[],
        settings: w.settings ?? null,
        versions: w.versions as Record<string, number>,
        self: w.self as number,
        roster: w.roster as Record<number, RosterEntry>,
      };
      if ("capacity" in w) {
        snap.capacity = w.capacity as number;
      }
      if ("provenance" in w) {
        snap.provenance = w.provenance as RoomSceneMeta | null;
      }
      if ("counts" in w) {
        snap.counts = w.counts as Record<string, CountCacheEntry>;
      }
      if ("clockSinceResetMs" in w) {
        snap.clockSinceResetMs = w.clockSinceResetMs as number;
      }
      return snap;
    }
    case MSG_T.presence:
      return {
        t: "presence",
        colors: w.colors as Record<number, number>,
        claims: w.claims as Record<string, number>,
        owner: w.owner as number,
        names: w.names as Record<number, string>,
        roster: w.roster as Record<number, string>,
        public: w.public === true,
      };
    case MSG_T.claim:
      return { t: "claim", id: w.id as string };
    case MSG_T.release:
      return { t: "release", id: w.id as string };
    case MSG_T.reset:
      return { t: "reset" };
    case MSG_T.rejected: {
      const r: RejectMsg = { t: "rejected", reason: w.reason as RejectMsg["reason"] };
      if ("r" in w) {
        r.retryAfterMs = w.r as number;
      }
      return r;
    }
    case MSG_T.emote:
      return { t: "emote", uid: w.uid as string, emoji: w.emoji as string };
    case MSG_T.button:
      return { t: "button", uid: w.uid as string, id: w.id as string, phase: w.phase as ButtonMsg["phase"] };
    case MSG_T.kick:
      return { t: "kick", target: w.target as string };
    case MSG_T.kickDone:
      return { t: "kickDone" };
    case MSG_T.activity: {
      const a: ActivityMsg = { t: "activity", uid: w.uid as string, kind: ACTIVITY_DEC[w.k as number] };
      if ("d" in w) {
        a.detail = w.d as string;
      }
      return a;
    }
    case MSG_T.count: {
      const c: CountMsg = { t: "count", id: w.i as string, value: w.v as number };
      if ("o" in w) {
        c.owner = w.o as number;
      }
      return c;
    }
    case MSG_T.countOwner: {
      const c: CountOwnerMsg = { t: "countOwner", id: w.i as string };
      if ("o" in w) {
        c.owner = w.o as number;
      }
      return c;
    }
    default:
      throw new Error(`decodeMsg: unknown wire type ${String(t)}`);
  }
}
